// Gesetzliche Feiertage — übernommen aus hotel-harmony-hub (src/lib/holidays.ts), ohne date-fns,
// damit derselbe Code in Deno und im Browser läuft.
//
// Feiertage werden nicht pro Jahr gespeichert: Jede Region ist ein Regelsatz aus festen Daten und
// beweglichen Festen relativ zu Ostern. Welche Regionen gelten, steht je Mandant in
// `tenants.holiday_regions` (z. B. ["IT-BZ"]). Eine Unterregion erbt die Feiertage ihres Landes.

import { addDays, toLocalDate, type LocalDate } from "./time.ts";

export type HolidayLanguage = "de" | "en" | "it";
type Names = Record<HolidayLanguage, string>;

interface HolidayRule {
  fixed?: [month: number, day: number];
  easterOffset?: number;
  name: Names;
}

export interface HolidayRegion {
  code: string;
  country: string;
  name: Names;
  rules: HolidayRule[];
}

export interface Holiday {
  date: LocalDate;
  region: string;
  name: Names;
}

const names = (de: string, en: string, it: string): Names => ({ de, en, it });
const fixed = (month: number, day: number, name: Names): HolidayRule => ({ fixed: [month, day], name });
const easter = (easterOffset: number, name: Names): HolidayRule => ({ easterOffset, name });

const NEW_YEAR = names("Neujahr", "New Year's Day", "Capodanno");
const EPIPHANY = names("Heilige Drei Könige", "Epiphany", "Epifania");
const GOOD_FRIDAY = names("Karfreitag", "Good Friday", "Venerdì Santo");
const EASTER_SUNDAY = names("Ostersonntag", "Easter Sunday", "Pasqua");
const EASTER_MONDAY = names("Ostermontag", "Easter Monday", "Lunedì dell'Angelo");
const LABOUR_DAY = names("Tag der Arbeit", "Labour Day", "Festa del Lavoro");
const ASCENSION = names("Christi Himmelfahrt", "Ascension Day", "Ascensione");
const WHIT_MONDAY = names("Pfingstmontag", "Whit Monday", "Lunedì di Pentecoste");
const CORPUS_CHRISTI = names("Fronleichnam", "Corpus Christi", "Corpus Domini");
const ASSUMPTION = names("Mariä Himmelfahrt", "Assumption Day", "Ferragosto");
const ALL_SAINTS = names("Allerheiligen", "All Saints' Day", "Ognissanti");
const IMMACULATE_CONCEPTION = names("Mariä Empfängnis", "Immaculate Conception", "Immacolata Concezione");
const CHRISTMAS = names("Weihnachten", "Christmas Day", "Natale");
const ST_STEPHEN = names("Stefanitag", "St. Stephen's Day", "Santo Stefano");

export const HOLIDAY_REGIONS: HolidayRegion[] = [
  {
    code: "IT",
    country: "IT",
    name: names("Italien", "Italy", "Italia"),
    rules: [
      fixed(1, 1, NEW_YEAR),
      fixed(1, 6, EPIPHANY),
      easter(0, EASTER_SUNDAY),
      easter(1, EASTER_MONDAY),
      fixed(4, 25, names("Tag der Befreiung", "Liberation Day", "Festa della Liberazione")),
      fixed(5, 1, LABOUR_DAY),
      fixed(6, 2, names("Tag der Republik", "Republic Day", "Festa della Repubblica")),
      fixed(8, 15, ASSUMPTION),
      fixed(11, 1, ALL_SAINTS),
      fixed(12, 8, IMMACULATE_CONCEPTION),
      fixed(12, 25, CHRISTMAS),
      fixed(12, 26, ST_STEPHEN),
    ],
  },
  {
    code: "IT-BZ",
    country: "IT",
    name: names("Südtirol", "South Tyrol", "Alto Adige"),
    rules: [easter(50, WHIT_MONDAY)],
  },
  {
    code: "DE",
    country: "DE",
    name: names("Deutschland", "Germany", "Germania"),
    rules: [
      fixed(1, 1, NEW_YEAR),
      easter(-2, GOOD_FRIDAY),
      easter(1, EASTER_MONDAY),
      fixed(5, 1, LABOUR_DAY),
      easter(39, ASCENSION),
      easter(50, WHIT_MONDAY),
      fixed(10, 3, names("Tag der Deutschen Einheit", "German Unity Day", "Giornata dell'Unità Tedesca")),
      fixed(12, 25, CHRISTMAS),
      fixed(12, 26, ST_STEPHEN),
    ],
  },
  {
    code: "AT",
    country: "AT",
    name: names("Österreich", "Austria", "Austria"),
    rules: [
      fixed(1, 1, NEW_YEAR),
      fixed(1, 6, EPIPHANY),
      easter(1, EASTER_MONDAY),
      fixed(5, 1, LABOUR_DAY),
      easter(39, ASCENSION),
      easter(50, WHIT_MONDAY),
      easter(60, CORPUS_CHRISTI),
      fixed(8, 15, ASSUMPTION),
      fixed(10, 26, names("Nationalfeiertag", "National Day", "Festa nazionale austriaca")),
      fixed(11, 1, ALL_SAINTS),
      fixed(12, 8, IMMACULATE_CONCEPTION),
      fixed(12, 25, CHRISTMAS),
      fixed(12, 26, ST_STEPHEN),
    ],
  },
];

const REGION_BY_CODE = new Map(HOLIDAY_REGIONS.map((region) => [region.code, region]));

/** Ostersonntag (gregorianisch, Meeus/Jones/Butcher) als Kalendertag. */
export function easterSunday(year: number): LocalDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return toLocalDate(year, month, day);
}

/** Unbekannte Codes fallen weg; eine Unterregion zieht ihr Land mit. */
export function normalizeHolidayRegions(codes: readonly string[] | null | undefined): string[] {
  const wanted = new Set<string>();
  for (const code of codes ?? []) {
    const region = REGION_BY_CODE.get(code);
    if (!region) continue;
    wanted.add(region.country);
    wanted.add(region.code);
  }
  return HOLIDAY_REGIONS.map((region) => region.code).filter((code) => wanted.has(code));
}

export function holidaysForYear(year: number, regionCodes: readonly string[]): Holiday[] {
  const easterDay = easterSunday(year);
  const seen = new Set<string>();
  const result: Holiday[] = [];
  for (const code of normalizeHolidayRegions(regionCodes)) {
    const region = REGION_BY_CODE.get(code)!;
    for (const rule of region.rules) {
      const date = rule.fixed
        ? toLocalDate(year, rule.fixed[0], rule.fixed[1])
        : addDays(easterDay, rule.easterOffset ?? 0);
      if (seen.has(date)) continue;
      seen.add(date);
      result.push({ date, region: region.code, name: rule.name });
    }
  }
  return result.sort((a, b) => a.date.localeCompare(b.date));
}

/** Feiertage zwischen zwei Kalendertagen (inklusive), nach Datum. */
export function holidaysByDay(
  from: LocalDate,
  to: LocalDate,
  regionCodes: readonly string[],
): Map<LocalDate, Holiday> {
  const map = new Map<LocalDate, Holiday>();
  for (let year = Number(from.slice(0, 4)); year <= Number(to.slice(0, 4)); year++) {
    for (const holiday of holidaysForYear(year, regionCodes)) {
      if (holiday.date >= from && holiday.date <= to) map.set(holiday.date, holiday);
    }
  }
  return map;
}
